import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-salon-reports',
  templateUrl: './salon-reports.component.html',
  styleUrls: ['./salon-reports.component.css']
})
export class SalonReportsComponent implements OnChanges {
  @Input() sucId = 1;
  @Input() fecha = '';

  active = 'trabajador';
  workers: any[] = [];
  workerDetails: any[] = [];
  rentabilidad: any[] = [];
  caja: any[] = [];
  cajaMovimientos: any[] = [];
  fecini = '';
  fecfin = '';
  loading = false;

  constructor(private api: ApiService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.fecini) {
      this.fecini = this.fecha;
      this.fecfin = this.fecha;
    }
    this.load();
  }

  load(): void {
    if (!this.fecini || !this.fecfin) {
      return;
    }
    this.loading = true;
    this.api.salonReporteTrabajador({
      p_suc_id: this.sucId,
      p_fecini: this.fecini,
      p_fecfin: this.fecfin
    }).subscribe((data: any) => {
      if (Array.isArray(data) && Array.isArray(data[0])) {
        this.workers = (data[0] || []).map((w: any) => Object.assign({ expanded: true }, w));
        this.workerDetails = Array.isArray(data[1]) ? data[1] : [];
      } else {
        this.workers = this.rows(data).map((w: any) => Object.assign({ expanded: true }, w));
        this.workerDetails = [];
      }
      this.loading = false;
    }, () => this.loading = false);

    this.api.salonReporteRentabilidad({
      p_fecini: this.fecini,
      p_fecfin: this.fecfin
    }).subscribe((data: any) => this.rentabilidad = this.rows(data));

    this.api.salonReporteCaja({
      p_suc_id: this.sucId,
      p_fecha: this.fecfin,
      p_fecini: this.fecini,
      p_fecfin: this.fecfin
    }).subscribe((data: any) => {
      if (Array.isArray(data) && Array.isArray(data[0])) {
        this.caja = data[0];
        this.cajaMovimientos = Array.isArray(data[1]) ? data[1] : [];
        this.metodosResumenBackend = Array.isArray(data[2]) ? data[2] : [];
        this.metodosPorDiaBackend = Array.isArray(data[3]) ? data[3] : [];
        return;
      }
      this.caja = this.rows(data);
      this.cajaMovimientos = [];
      this.metodosResumenBackend = [];
      this.metodosPorDiaBackend = [];
    }, () => {
      this.caja = [];
      this.cajaMovimientos = [];
      this.metodosResumenBackend = [];
      this.metodosPorDiaBackend = [];
    });
  }

  metodosResumenBackend: any[] = [];
  metodosPorDiaBackend: any[] = [];
  readonly standardPaymentMethods = ['EFECTIVO', 'YAPE', 'PLIN', 'TARJETA', 'TRANSFERENCIA'];

  get ingresosMovimientos(): any[] {
    return (this.cajaMovimientos || []).filter(m => m && m.cam_tipo === 'INGRESO');
  }

  get totalIngresosMetodos(): number {
    return this.ingresosMovimientos.reduce((sum, m) => sum + Number(m.cam_monto || 0), 0);
  }

  getMetodoTotal(nombre: string): number {
    const norm = (nombre || '').trim().toUpperCase();
    return this.ingresosMovimientos
      .filter(m => (m.mpa_nombre || 'EFECTIVO').trim().toUpperCase() === norm)
      .reduce((sum, m) => sum + Number(m.cam_monto || 0), 0);
  }

  getMetodoCount(nombre: string): number {
    const norm = (nombre || '').trim().toUpperCase();
    return this.ingresosMovimientos
      .filter(m => (m.mpa_nombre || 'EFECTIVO').trim().toUpperCase() === norm).length;
  }

  getMetodoPct(nombre: string): number {
    const total = this.totalIngresosMetodos;
    if (total <= 0) return 0;
    return (this.getMetodoTotal(nombre) / total) * 100;
  }

  get totalPorMetodoPago(): { nombre: string, total: number, count: number, pct: number }[] {
    const totalGeneral = this.totalIngresosMetodos;
    const mapa: { [key: string]: { total: number, count: number } } = {};

    this.standardPaymentMethods.forEach(nombre => {
      mapa[nombre] = { total: 0, count: 0 };
    });

    this.ingresosMovimientos.forEach(m => {
      const nombre = (m.mpa_nombre || 'EFECTIVO').trim().toUpperCase();
      if (!mapa[nombre]) {
        mapa[nombre] = { total: 0, count: 0 };
      }
      mapa[nombre].total += Number(m.cam_monto || 0);
      mapa[nombre].count += 1;
    });

    const resultado: { nombre: string, total: number, count: number, pct: number }[] = [];

    this.standardPaymentMethods.forEach(nombre => {
      if (mapa[nombre]) {
        const t = mapa[nombre].total;
        const c = mapa[nombre].count;
        resultado.push({
          nombre,
          total: t,
          count: c,
          pct: totalGeneral > 0 ? (t / totalGeneral) * 100 : 0
        });
      }
    });

    Object.keys(mapa).forEach(nombre => {
      if (!this.standardPaymentMethods.includes(nombre)) {
        const t = mapa[nombre].total;
        const c = mapa[nombre].count;
        resultado.push({
          nombre,
          total: t,
          count: c,
          pct: totalGeneral > 0 ? (t / totalGeneral) * 100 : 0
        });
      }
    });

    return resultado;
  }

  get desglosePorDiaMetodoPago(): any[] {
    const diasMap: { [fecha: string]: { fecha: string, EFECTIVO: number, YAPE: number, PLIN: number, TARJETA: number, TRANSFERENCIA: number, total: number } } = {};

    (this.caja || []).forEach(c => {
      const f = c.caj_fecha;
      if (f && !diasMap[f]) {
        diasMap[f] = {
          fecha: f,
          total: 0,
          EFECTIVO: 0,
          YAPE: 0,
          PLIN: 0,
          TARJETA: 0,
          TRANSFERENCIA: 0
        };
      }
    });

    this.ingresosMovimientos.forEach(m => {
      let fecha = m.cam_fecha ? String(m.cam_fecha).substring(0, 10) : '';
      if (!fecha) {
        fecha = this.cajaResumen.caj_fecha || this.fecfin;
      }
      if (!diasMap[fecha]) {
        diasMap[fecha] = {
          fecha: fecha,
          total: 0,
          EFECTIVO: 0,
          YAPE: 0,
          PLIN: 0,
          TARJETA: 0,
          TRANSFERENCIA: 0
        };
      }
      const metodo = (m.mpa_nombre || 'EFECTIVO').trim().toUpperCase();
      const monto = Number(m.cam_monto || 0);
      if (diasMap[fecha][metodo] !== undefined) {
        diasMap[fecha][metodo] += monto;
      } else {
        (diasMap[fecha] as any)[metodo] = monto;
      }
      diasMap[fecha].total += monto;
    });

    return Object.values(diasMap).sort((a, b) => b.fecha.localeCompare(a.fecha));
  }

  getWorkerServices(traId: number): any[] {
    return (this.workerDetails || []).filter(item => Number(item.tra_id) === Number(traId));
  }

  toggleWorkerExpand(worker: any): void {
    worker.expanded = !worker.expanded;
  }

  money(value: any): string {
    return `S/ ${Number(value || 0).toFixed(2)}`;
  }

  total(rows: any[], field: string): number {
    return rows.reduce((sum, row) => sum + Number(row && row[field] ? row[field] : 0), 0);
  }

  get cajaResumen(): any {
    return this.caja && this.caja.length ? this.caja[0] : {};
  }

  get ingresosCaja(): number {
    if (!this.caja || !this.caja.length) return 0;
    return this.caja.reduce((acc, c) => acc + Number(c.ingresos || 0), 0);
  }

  get egresosCaja(): number {
    if (!this.caja || !this.caja.length) return 0;
    return this.caja.reduce((acc, c) => acc + Number(c.egresos || 0), 0);
  }

  get aperturaCaja(): number {
    if (!this.caja || !this.caja.length) return 0;
    return this.caja.reduce((acc, c) => acc + Number(c.caj_apertura || 0), 0);
  }

  get saldoCaja(): number {
    if (!this.caja || !this.caja.length) return 0;
    return this.caja.reduce((acc, c) => acc + Number(c.saldo_calculado || 0), 0);
  }

  private rows(data: any): any[] {
    if (Array.isArray(data)) {
      if (Array.isArray(data[0])) {
        return data[0];
      }
      return data;
    }
    return [];
  }
}
